/**
 * Dialog de criação/edição de store — TH-53160.
 *
 * Sem `store` prop → modo create (POST /stores).
 * Com `store` prop → modo edit (PATCH /stores/:id).
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";

const UFS = [
  "", "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO",
  "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI",
  "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

// Latitude/longitude opcionais — admin pode cadastrar a loja sem
// georreferência (TH-55102, aba "Mapa"). Aceitamos string no form e
// convertemos pra number antes de mandar; vazio vira null.
const coordSchema = z
  .string()
  .optional()
  .or(z.literal(""))
  .refine(
    (v) => v === undefined || v === "" || !Number.isNaN(Number(v)),
    "número inválido",
  );

const schema = z
  .object({
    name: z.string().min(1, "Nome obrigatório").max(200),
    code: z.string().max(64).optional().or(z.literal("")),
    address: z.string().max(500).optional().or(z.literal("")),
    city: z.string().max(120).optional().or(z.literal("")),
    state: z.string().optional().or(z.literal("")),
    latitude: coordSchema,
    longitude: coordSchema,
  })
  .superRefine((data, ctx) => {
    if (data.latitude && data.latitude !== "") {
      const n = Number(data.latitude);
      if (n < -90 || n > 90) {
        ctx.addIssue({
          code: "custom",
          path: ["latitude"],
          message: "deve estar entre -90 e 90",
        });
      }
    }
    if (data.longitude && data.longitude !== "") {
      const n = Number(data.longitude);
      if (n < -180 || n > 180) {
        ctx.addIssue({
          code: "custom",
          path: ["longitude"],
          message: "deve estar entre -180 e 180",
        });
      }
    }
  });

type FormData = z.infer<typeof schema>;

export interface Store {
  id: string;
  name: string;
  code: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  // Postgres NUMERIC vira string no JSON (preserva precisão). Tratamos
  // como string|number|null no front e convertemos antes de usar.
  latitude: string | number | null;
  longitude: string | number | null;
  active: boolean;
  tv_count: number;
}

async function saveStore(
  store: Store | undefined,
  data: FormData,
): Promise<void> {
  const url = store ? `/api/stores/${store.id}` : "/api/stores";
  const method = store ? "PATCH" : "POST";
  // Strings vazias viram null no backend. Lat/lng vão como number.
  const payload: Record<string, string | number | null> = { name: data.name };
  for (const k of ["code", "address", "city", "state"] as const) {
    payload[k] = data[k]?.trim() ? data[k]!.trim() : null;
  }
  for (const k of ["latitude", "longitude"] as const) {
    const raw = data[k]?.trim();
    payload[k] = raw ? Number(raw) : null;
  }
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  store?: Store;
}

export function StoreFormDialog({ open, onOpenChange, store }: Props) {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const coordToString = (v: string | number | null | undefined): string =>
    v === null || v === undefined ? "" : String(v);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: store?.name ?? "",
      code: store?.code ?? "",
      address: store?.address ?? "",
      city: store?.city ?? "",
      state: store?.state ?? "",
      latitude: coordToString(store?.latitude),
      longitude: coordToString(store?.longitude),
    },
  });

  // Repopula quando muda de store (edit -> outro edit) ou abre novo create.
  useEffect(() => {
    if (open) {
      reset({
        name: store?.name ?? "",
        code: store?.code ?? "",
        address: store?.address ?? "",
        city: store?.city ?? "",
        state: store?.state ?? "",
        latitude: coordToString(store?.latitude),
        longitude: coordToString(store?.longitude),
      });
      setServerError(null);
    }
  }, [open, store, reset]);

  const mutation = useMutation({
    mutationFn: (data: FormData) => saveStore(store, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      onOpenChange(false);
    },
    onError: (err: Error) => setServerError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>{store ? "Editar loja" : "Nova loja"}</DialogTitle>

        <form
          onSubmit={handleSubmit((data) => mutation.mutate(data))}
          className="mt-4 flex flex-col gap-4"
          noValidate
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="store-name">Nome</Label>
            <Input
              id="store-name"
              autoFocus
              aria-invalid={errors.name ? true : undefined}
              {...register("name")}
            />
            {errors.name ? (
              <span role="alert" className="text-xs text-[var(--color-destructive)]">
                {errors.name.message}
              </span>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="store-code">Código</Label>
              <Input id="store-code" {...register("code")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="store-state">UF</Label>
              <Select id="store-state" {...register("state")}>
                {UFS.map((uf) => (
                  <option key={uf || "none"} value={uf}>
                    {uf || "—"}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="store-city">Cidade</Label>
            <Input id="store-city" {...register("city")} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="store-address">Endereço</Label>
            <Input id="store-address" {...register("address")} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="store-latitude">Latitude</Label>
              <Input
                id="store-latitude"
                type="text"
                inputMode="decimal"
                placeholder="ex: -23.550520"
                aria-invalid={errors.latitude ? true : undefined}
                {...register("latitude")}
              />
              {errors.latitude ? (
                <span role="alert" className="text-xs text-[var(--color-destructive)]">
                  {errors.latitude.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="store-longitude">Longitude</Label>
              <Input
                id="store-longitude"
                type="text"
                inputMode="decimal"
                placeholder="ex: -46.633308"
                aria-invalid={errors.longitude ? true : undefined}
                {...register("longitude")}
              />
              {errors.longitude ? (
                <span role="alert" className="text-xs text-[var(--color-destructive)]">
                  {errors.longitude.message}
                </span>
              ) : null}
            </div>
          </div>
          <p className="-mt-2 text-xs text-[var(--color-muted-foreground)]">
            Opcional. Lojas com lat/lng aparecem na aba "Mapa".
          </p>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {serverError}
            </div>
          ) : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando…" : store ? "Salvar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
